/**
 * ESG Regulatory Compliance Component
 * 
 * Comprehensive regulatory compliance dashboard providing:
 * - Multi-framework compliance monitoring and scoring
 * - Real-time violation tracking and remediation management
 * - Risk assessment and trend analysis
 * - Regulatory update monitoring and impact assessment
 * - Audit trail management and compliance reporting
 * - Executive dashboard with key insights and recommendations
 * 
 * Features:
 * - 6-tab interface: Overview, Frameworks, Violations, Risk Assessment, Updates, Audit Trail
 * - Interactive compliance scoring and trend visualization
 * - Real-time violation management with remediation tracking
 * - Comprehensive risk assessment with mitigation strategies
 * - Regulatory change monitoring and impact analysis
 * - Detailed audit trail with filterable activity history
 * - Executive summary with actionable insights
 * 
 * @version 1.0.0
 * @created 2025-09-23
 */

import React, { useState, useEffect } from 'react';
import esgRegulatoryComplianceService from '../../../services/esg/esgRegulatoryComplianceService';

const ESGRegulatoryCompliance = () => {
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => {
    loadComplianceData();
    
    // Subscribe to data updates
    const unsubscribe = esgRegulatoryComplianceService.subscribe((data) => {
      setComplianceData(data);
    });

    return unsubscribe;
  }, []);

  const loadComplianceData = async () => {
    try {
      setError(null);
      const data = await esgRegulatoryComplianceService.getComplianceData();
      setComplianceData(data);
    } catch (err) {
      console.error('Error loading compliance data:', err);
      setError('Failed to load compliance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await esgRegulatoryComplianceService.refreshData();
    } catch (err) {
      setError('Failed to refresh data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'compliant': return '#10B981';
      case 'partially compliant': return '#F59E0B';
      case 'non-compliant': return '#EF4444';
      case 'under review': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return '#ef4444';
      case 'high': return '#f87171';
      case 'medium': return '#fbbf24';
      case 'low': return '#22c55e';
      case 'info': return '#60a5fa';
      default: return '#9ca3af';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return '#ef4444';
      case 'medium': return '#fbbf24';
      case 'low': return '#22c55e';
      default: return '#9ca3af';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#22c55e';
    if (score >= 75) return '#fbbf24';
    if (score >= 60) return '#fb923c';
    return '#f87171';
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid rgba(124, 58, 237, 0.3)',
            borderTopColor: '#7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '600' }}>Loading regulatory compliance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '800', marginBottom: '15px' }}>Error Loading Compliance Data</h2>
          <p style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '25px', fontWeight: '500' }}>{error}</p>
          <button 
            onClick={loadComplianceData}
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.4)';
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      padding: '0'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        color: 'white',
        padding: '50px 30px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.4
        }}></div>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  padding: '15px',
                  borderRadius: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <span style={{ fontSize: '40px' }}>⚖️</span>
                </div>
                <div>
                  <h1 style={{ fontSize: '42px', fontWeight: '800', margin: 0, textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)' }}>
                    ESG Regulatory Compliance
                  </h1>
                  <p style={{ fontSize: '18px', margin: '5px 0 0 0', color: '#e9d5ff', fontWeight: '500' }}>
                    Comprehensive regulatory compliance monitoring and management across 15+ frameworks
                  </p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <button 
                onClick={handleRefresh} 
                disabled={refreshing}
                style={{
                  background: refreshing ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: refreshing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: refreshing ? 0.6 : 1
                }}
                onMouseEnter={(e) => !refreshing && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
              >
                <span style={{ fontSize: '18px' }}>{refreshing ? '⟳' : '🔄'}</span>
                {refreshing ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: '0 30px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '5px', overflowX: 'auto' }}>
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'frameworks', label: 'Frameworks', icon: '📋' },
            { id: 'violations', label: 'Violations', icon: '⚠️' },
            { id: 'risk', label: 'Risk Assessment', icon: '🎯' },
            { id: 'updates', label: 'Updates', icon: '📢' },
            { id: 'audit', label: 'Audit Trail', icon: '📝' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: activeTab === tab.id ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#94a3b8',
                border: 'none',
                padding: '16px 24px',
                fontSize: '15px',
                fontWeight: activeTab === tab.id ? '700' : '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderBottom: activeTab === tab.id ? '3px solid #c084fc' : '3px solid transparent',
                whiteSpace: 'nowrap',
                borderRadius: activeTab === tab.id ? '8px 8px 0 0' : '0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.color = '#cbd5e1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#94a3b8';
                }
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px' }}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div style={{ padding: '0' }}>
              <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                📊 Compliance Overview
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>
                Comprehensive regulatory compliance status across all monitored frameworks
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '25px', marginBottom: '30px' }}>
                {/* Overall Compliance Score Card */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '30px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <h2 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', textAlign: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    Overall Compliance Score
                  </h2>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '25px' }}>
                    <div style={{
                      position: 'relative',
                      width: '180px',
                      height: '180px',
                      background: `conic-gradient(${getScoreColor(complianceData.overallScore)} 0deg ${complianceData.overallScore * 3.6}deg, rgba(255, 255, 255, 0.1) ${complianceData.overallScore * 3.6}deg 360deg)`,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                      <div style={{
                        width: '140px',
                        height: '140px',
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{ color: '#ffffff', fontSize: '48px', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                          {complianceData.overallScore}
                        </div>
                        <div style={{ color: getScoreColor(complianceData.overallScore), fontSize: '20px', fontWeight: '700', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {complianceData.complianceGrade}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <p style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', margin: 0, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Risk Level:</span>
                      <span style={{ color: getRiskLevelColor(complianceData.riskLevel), fontWeight: '700', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                        {complianceData.riskLevel}
                      </span>
                    </p>
                    <p style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', margin: 0, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Last Audit:</span>
                      <span style={{ color: '#ffffff', fontWeight: '700' }}>{new Date(complianceData.lastAuditDate).toLocaleDateString()}</span>
                    </p>
                    <p style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', margin: 0, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#94a3b8' }}>Next Audit:</span>
                      <span style={{ color: '#fbbf24', fontWeight: '700', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{new Date(complianceData.nextAuditDate).toLocaleDateString()}</span>
                    </p>
                  </div>
                </div>

                {/* Compliance Summary Card */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '30px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', textAlign: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    Compliance Summary
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                    <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '12px' }}>
                      <div style={{ color: '#60a5fa', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                        {complianceData.totalFrameworks}
                      </div>
                      <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Total Frameworks
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '12px' }}>
                      <div style={{ color: '#22c55e', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                        {complianceData.compliantFrameworks}
                      </div>
                      <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Compliant
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '12px' }}>
                      <div style={{ color: '#fbbf24', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                        {complianceData.partialCompliance}
                      </div>
                      <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Partial
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '12px' }}>
                      <div style={{ color: '#ef4444', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                        {complianceData.nonCompliantFrameworks}
                      </div>
                      <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Non-Compliant
                      </div>
                    </div>
                  </div>
                </div>

                {/* KPI Card */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '30px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', textAlign: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    Key Performance Indicators
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {complianceData.complianceKPIs.map((kpi, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 15px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '10px',
                        borderLeft: `4px solid ${kpi.trend === 'improving' ? '#22c55e' : kpi.trend === 'declining' ? '#ef4444' : '#94a3b8'}`
                      }}>
                        <div style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600' }}>
                          {kpi.name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                          <span style={{
                            color: kpi.trend === 'improving' ? '#22c55e' : kpi.trend === 'declining' ? '#ef4444' : '#94a3b8',
                            fontSize: '18px',
                            fontWeight: '800',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                          }}>
                            {kpi.value}{kpi.unit === 'percentage' ? '%' : kpi.unit === 'days' ? 'd' : ''}
                          </span>
                          <span style={{ color: '#64748b', fontSize: '14px', fontWeight: '600' }}>
                            /{kpi.target}{kpi.unit === 'percentage' ? '%' : kpi.unit === 'days' ? 'd' : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Compliance Trends */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '30px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                marginBottom: '30px'
              }}>
                <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '25px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  📈 Compliance Trends
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ display: 'flex', gap: '20px', minWidth: '800px', alignItems: 'flex-end', height: '300px', padding: '20px' }}>
                    {complianceData.complianceHistory.map((item, index) => (
                      <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textAlign: 'center', minHeight: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '4px' }}>
                          <div style={{ color: getScoreColor(item.score), fontSize: '18px', fontWeight: '800', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {item.score}%
                          </div>
                          <div style={{ color: item.violations > 5 ? '#ef4444' : '#fbbf24', fontSize: '11px', fontWeight: '600' }}>
                            {item.violations} violations
                          </div>
                        </div>
                        <div style={{
                          width: '100%',
                          height: `${item.score * 2}px`,
                          background: `linear-gradient(to top, ${getScoreColor(item.score)}, ${getScoreColor(item.score)}99)`,
                          borderRadius: '8px 8px 0 0',
                          transition: 'all 0.3s ease',
                          boxShadow: `0 -4px 15px ${getScoreColor(item.score)}40`,
                          position: 'relative'
                        }}></div>
                        <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {item.month}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Executive Summary */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '30px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '25px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  📋 Executive Summary
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px' }}>
                  {/* Key Achievements */}
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    borderRadius: '16px',
                    padding: '25px',
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    boxShadow: '0 4px 15px rgba(34, 197, 94, 0.2)'
                  }}>
                    <h4 style={{ color: '#22c55e', fontSize: '18px', fontWeight: '800', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                      <span style={{ fontSize: '22px' }}>✅</span>
                      Key Achievements
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {complianceData.executiveSummary.keyAchievements.map((achievement, index) => (
                        <li key={index} style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Top Risks */}
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '16px',
                    padding: '25px',
                    border: '2px solid rgba(239, 68, 68, 0.3)',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.2)'
                  }}>
                    <h4 style={{ color: '#ef4444', fontSize: '18px', fontWeight: '800', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                      <span style={{ fontSize: '22px' }}>⚠️</span>
                      Top Risks
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {complianceData.executiveSummary.topRisks.map((risk, index) => (
                        <li key={index} style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Recommended Actions */}
                  <div style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '16px',
                    padding: '25px',
                    border: '2px solid rgba(59, 130, 246, 0.3)',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.2)'
                  }}>
                    <h4 style={{ color: '#3b82f6', fontSize: '18px', fontWeight: '800', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                      <span style={{ fontSize: '22px' }}>🎯</span>
                      Recommended Actions
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {complianceData.executiveSummary.recommendedActions.map((action, index) => (
                        <li key={index} style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Frameworks Tab */}
          {activeTab === 'frameworks' && (
            <div style={{ padding: '0' }}>
              <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                📋 Regulatory Frameworks
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>
                Detailed compliance status for all monitored regulatory frameworks
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '25px' }}>
                {complianceData.regulatoryFrameworks.map((framework) => (
                  <div key={framework.id} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    padding: '25px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px'
                  }}>
                    {/* Framework Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: '800', marginBottom: '10px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {framework.name}
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{
                            background: 'rgba(124, 58, 237, 0.2)',
                            color: '#c084fc',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '1px solid rgba(124, 58, 237, 0.3)'
                          }}>
                            {framework.jurisdiction}
                          </span>
                          <span style={{
                            background: 'rgba(59, 130, 246, 0.2)',
                            color: '#60a5fa',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '1px solid rgba(59, 130, 246, 0.3)'
                          }}>
                            {framework.category}
                          </span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          color: getScoreColor(framework.score),
                          fontSize: '32px',
                          fontWeight: '900',
                          lineHeight: 1,
                          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}>
                          {framework.score}/100
                        </div>
                        <div style={{
                          color: getStatusColor(framework.status),
                          fontSize: '13px',
                          fontWeight: '700',
                          marginTop: '5px',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          {framework.status}
                        </div>
                      </div>
                    </div>

                    {/* Framework Details */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      padding: '15px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600' }}>Weight:</span>
                        <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '700' }}>{framework.weight}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600' }}>Risk Level:</span>
                        <span style={{
                          color: getRiskLevelColor(framework.riskLevel),
                          fontSize: '15px',
                          fontWeight: '700',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          {framework.riskLevel}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600' }}>Last Assessment:</span>
                        <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600' }}>
                          {new Date(framework.lastAssessment).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600' }}>Next Review:</span>
                        <span style={{ color: '#fbbf24', fontSize: '14px', fontWeight: '700', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                          {new Date(framework.nextReview).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600' }}>Certificate Status:</span>
                        <span style={{
                          color: framework.certificateStatus === 'Valid' ? '#22c55e' : '#fbbf24',
                          fontSize: '15px',
                          fontWeight: '700',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          {framework.certificateStatus}
                        </span>
                      </div>
                    </div>

                    {/* Requirements Breakdown */}
                    <div>
                      <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '800', marginBottom: '12px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                        Requirements Breakdown
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                        {Object.entries(framework.requirements).map(([req, score]) => (
                          <div key={req} style={{
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '10px',
                            padding: '10px 12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderLeft: `3px solid ${score >= 90 ? '#22c55e' : score >= 75 ? '#fbbf24' : '#ef4444'}`
                          }}>
                            <span style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '600' }}>{req}</span>
                            <span style={{
                              color: score >= 90 ? '#22c55e' : score >= 75 ? '#fbbf24' : '#ef4444',
                              fontSize: '15px',
                              fontWeight: '800',
                              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                            }}>
                              {score}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Active Violations */}
                    {framework.violations.length > 0 && (
                      <div style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: '12px',
                        padding: '15px',
                        border: '1px solid rgba(239, 68, 68, 0.3)'
                      }}>
                        <h4 style={{ color: '#ef4444', fontSize: '16px', fontWeight: '800', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                          <span>⚠️</span>
                          Active Violations ({framework.violations.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {framework.violations.map((violation) => (
                            <div key={violation.id} style={{
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              padding: '12px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '700' }}>{violation.id}</span>
                                <span style={{
                                  background: getSeverityColor(violation.severity),
                                  color: '#ffffff',
                                  padding: '3px 10px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '800',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  {violation.severity}
                                </span>
                              </div>
                              <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600', marginBottom: '6px', lineHeight: '1.5' }}>
                                {violation.description}
                              </div>
                              <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>
                                Status: <span style={{ color: '#cbd5e1', fontWeight: '700' }}>{violation.status}</span> | 
                                Expected: <span style={{ color: '#fbbf24', fontWeight: '700' }}>{new Date(violation.expectedResolution).toLocaleDateString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Violations Tab */}
          {activeTab === 'violations' && (
            <div style={{ padding: '0' }}>
              <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                ⚠️ Compliance Violations
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>
                Active compliance violations and remediation tracking
              </p>
              
              {/* Violations Summary & Filter */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                {/* Summary Cards */}
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', flex: 1 }}>
                  <div style={{
                    background: 'rgba(96, 165, 250, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '2px solid rgba(96, 165, 250, 0.3)',
                    textAlign: 'center',
                    minWidth: '130px'
                  }}>
                    <div style={{ color: '#60a5fa', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                      {complianceData.activeViolations.length}
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Active Violations
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '2px solid rgba(239, 68, 68, 0.3)',
                    textAlign: 'center',
                    minWidth: '110px'
                  }}>
                    <div style={{ color: '#ef4444', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                      {complianceData.activeViolations.filter(v => v.severity === 'Critical').length}
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Critical
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(251, 146, 60, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '2px solid rgba(251, 146, 60, 0.3)',
                    textAlign: 'center',
                    minWidth: '110px'
                  }}>
                    <div style={{ color: '#fb923c', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                      {complianceData.activeViolations.filter(v => v.severity === 'High').length}
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      High
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(251, 191, 36, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '2px solid rgba(251, 191, 36, 0.3)',
                    textAlign: 'center',
                    minWidth: '110px'
                  }}>
                    <div style={{ color: '#fbbf24', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                      {complianceData.activeViolations.filter(v => v.severity === 'Medium').length}
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Medium
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    textAlign: 'center',
                    minWidth: '110px'
                  }}>
                    <div style={{ color: '#22c55e', fontSize: '36px', fontWeight: '900', marginBottom: '8px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                      {complianceData.activeViolations.filter(v => v.severity === 'Low').length}
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Low
                    </div>
                  </div>
                </div>

                {/* Filter Dropdown */}
                <div>
                  <select 
                    value={filterSeverity} 
                    onChange={(e) => setFilterSeverity(e.target.value)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      padding: '12px 20px',
                      color: '#ffffff',
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      outline: 'none',
                      minWidth: '180px'
                    }}
                  >
                    <option value="all" style={{ background: '#1e293b', color: '#ffffff' }}>All Severities</option>
                    <option value="Critical" style={{ background: '#1e293b', color: '#ffffff' }}>Critical</option>
                    <option value="High" style={{ background: '#1e293b', color: '#ffffff' }}>High</option>
                    <option value="Medium" style={{ background: '#1e293b', color: '#ffffff' }}>Medium</option>
                    <option value="Low" style={{ background: '#1e293b', color: '#ffffff' }}>Low</option>
                  </select>
                </div>
              </div>

              {/* Violations List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {complianceData.activeViolations
                  .filter(violation => filterSeverity === 'all' || violation.severity === filterSeverity)
                  .map((violation) => (
                  <div key={violation.id} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    padding: '25px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    borderLeft: `5px solid ${getSeverityColor(violation.severity)}`
                  }}>
                    {/* Violation Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: '800', marginBottom: '10px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {violation.title}
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{
                            background: 'rgba(148, 163, 184, 0.2)',
                            color: '#cbd5e1',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '1px solid rgba(148, 163, 184, 0.3)'
                          }}>
                            {violation.id}
                          </span>
                          <span style={{
                            background: 'rgba(124, 58, 237, 0.2)',
                            color: '#c084fc',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '1px solid rgba(124, 58, 237, 0.3)'
                          }}>
                            {violation.framework}
                          </span>
                        </div>
                      </div>
                      <span style={{
                        background: getSeverityColor(violation.severity),
                        color: '#ffffff',
                        padding: '8px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: `0 4px 15px ${getSeverityColor(violation.severity)}40`
                      }}>
                        {violation.severity}
                      </span>
                    </div>

                    {/* Violation Description */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      padding: '15px',
                      marginBottom: '15px'
                    }}>
                      <p style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', lineHeight: '1.6', margin: 0 }}>
                        {violation.description}
                      </p>
                    </div>

                    {/* Violation Details Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '15px',
                      marginBottom: '15px'
                    }}>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '10px',
                        padding: '12px 15px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                      }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Date Identified
                        </span>
                        <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '700' }}>
                          {new Date(violation.dateIdentified).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '10px',
                        padding: '12px 15px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                      }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Status
                        </span>
                        <span style={{ color: '#60a5fa', fontSize: '15px', fontWeight: '700' }}>
                          {violation.status}
                        </span>
                      </div>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '10px',
                        padding: '12px 15px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                      }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Assigned To
                        </span>
                        <span style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700' }}>
                          {violation.assignedTo}
                        </span>
                      </div>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '10px',
                        padding: '12px 15px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                      }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Expected Resolution
                        </span>
                        <span style={{ color: '#fbbf24', fontSize: '15px', fontWeight: '700', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                          {new Date(violation.expectedResolution).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Business Impact */}
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '12px',
                      padding: '15px',
                      marginBottom: '15px',
                      border: '1px solid rgba(239, 68, 68, 0.3)'
                    }}>
                      <h4 style={{ color: '#ef4444', fontSize: '16px', fontWeight: '800', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                        <span>💼</span>
                        Business Impact
                      </h4>
                      <p style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6', margin: 0 }}>
                        {violation.businessImpact}
                      </p>
                    </div>

                    {/* Remediation Plan */}
                    <div style={{
                      background: 'rgba(59, 130, 246, 0.1)',
                      borderRadius: '12px',
                      padding: '15px',
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      <h4 style={{ color: '#3b82f6', fontSize: '16px', fontWeight: '800', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                        <span>🔧</span>
                        Remediation Plan
                      </h4>
                      <p style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6', margin: 0 }}>
                        {violation.remediationPlan}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* No Violations Message */}
              {complianceData.activeViolations.filter(violation => 
                filterSeverity === 'all' || violation.severity === filterSeverity
              ).length === 0 && (
                <div style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '40px',
                  textAlign: 'center',
                  border: '2px solid rgba(34, 197, 94, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>✅</div>
                  <h3 style={{ color: '#22c55e', fontSize: '24px', fontWeight: '800', marginBottom: '10px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    No violations found
                  </h3>
                  <p style={{ color: '#cbd5e1', fontSize: '16px', fontWeight: '600', margin: 0 }}>
                    {filterSeverity === 'all' 
                      ? 'There are no active violations at this time.' 
                      : `No ${filterSeverity.toLowerCase()} severity violations found.`
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Risk Assessment Tab */}
          {activeTab === 'risk' && (
            <div style={{ padding: '0' }}>
              <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                🎯 Risk Assessment
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>
                Comprehensive risk analysis and mitigation strategies
              </p>
              
              {/* Risk Overview Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px', marginBottom: '30px' }}>
                {/* Overall Risk Score Card */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '30px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  textAlign: 'center'
                }}>
                  <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    Overall Risk Score
                  </h3>
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      color: getRiskLevelColor(complianceData.riskAssessment.riskLevel),
                      fontSize: '56px',
                      fontWeight: '900',
                      lineHeight: 1,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      marginBottom: '10px'
                    }}>
                      {complianceData.riskAssessment.overallRiskScore}/100
                    </div>
                    <div style={{
                      display: 'inline-block',
                      background: getRiskLevelColor(complianceData.riskAssessment.riskLevel),
                      color: '#ffffff',
                      padding: '8px 24px',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      boxShadow: `0 4px 15px ${getRiskLevelColor(complianceData.riskAssessment.riskLevel)}40`
                    }}>
                      {complianceData.riskAssessment.riskLevel} Risk
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '15px',
                    marginTop: '20px'
                  }}>
                    <p style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600', margin: 0, fontStyle: 'italic' }}>
                      Lower scores indicate better compliance posture
                    </p>
                  </div>
                </div>

                {/* Risk Trends Card */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: '30px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  <h3 style={{ color: '#ffffff', fontSize: '22px', fontWeight: '800', marginBottom: '25px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    📉 Risk Trends
                  </h3>
                  <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '200px', gap: '10px' }}>
                    {complianceData.riskAssessment.riskTrends.map((trend, index) => (
                      <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          color: getRiskLevelColor(trend.score > 30 ? 'high' : trend.score > 20 ? 'medium' : 'low'),
                          fontSize: '16px',
                          fontWeight: '800',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          {trend.score}
                        </div>
                        <div style={{
                          width: '100%',
                          height: `${(100 - trend.score) * 1.5}px`,
                          maxHeight: '150px',
                          background: `linear-gradient(to top, ${getRiskLevelColor(trend.score > 30 ? 'high' : trend.score > 20 ? 'medium' : 'low')}, ${getRiskLevelColor(trend.score > 30 ? 'high' : trend.score > 20 ? 'medium' : 'low')}99)`,
                          borderRadius: '8px 8px 0 0',
                          boxShadow: `0 -4px 15px ${getRiskLevelColor(trend.score > 30 ? 'high' : trend.score > 20 ? 'medium' : 'low')}40`,
                          transition: 'all 0.3s ease'
                        }}></div>
                        <div style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {trend.month}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* High Risk Areas */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '30px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '800', marginBottom: '25px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                  🔴 High Risk Areas
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                  {complianceData.riskAssessment.highRiskAreas.map((area, index) => (
                    <div key={index} style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '16px',
                      padding: '25px',
                      border: '2px solid rgba(239, 68, 68, 0.3)',
                      boxShadow: '0 4px 15px rgba(239, 68, 68, 0.2)',
                      transition: 'all 0.3s ease'
                    }}>
                      {/* Risk Area Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '15px' }}>
                        <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', margin: 0, flex: 1, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                          {area.area}
                        </h4>
                        <div style={{
                          background: getRiskLevelColor('high'),
                          color: '#ffffff',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          fontSize: '18px',
                          fontWeight: '900',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          boxShadow: `0 4px 15px ${getRiskLevelColor('high')}40`,
                          whiteSpace: 'nowrap'
                        }}>
                          {area.riskScore}/100
                        </div>
                      </div>

                      {/* Affected Frameworks */}
                      <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: '700', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Affected Frameworks:
                        </h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {area.frameworks.map((framework, idx) => (
                            <span key={idx} style={{
                              background: 'rgba(124, 58, 237, 0.3)',
                              color: '#e9d5ff',
                              padding: '6px 14px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '700',
                              border: '1px solid rgba(124, 58, 237, 0.5)',
                              whiteSpace: 'nowrap'
                            }}>
                              {framework}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Mitigation Strategy */}
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '12px',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <h5 style={{ color: '#22c55e', fontSize: '14px', fontWeight: '800', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                          <span>✅</span>
                          Mitigation Strategy:
                        </h5>
                        <p style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6', margin: 0 }}>
                          {area.mitigation}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Updates Tab */}
          {activeTab === 'updates' && (
            <div style={{ padding: '0' }}>
              <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                📢 Regulatory Updates
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>
                Recent regulatory changes and their impact on compliance requirements
              </p>
              
              {/* Updates List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {complianceData.regulatoryUpdates.map((update, index) => (
                  <div key={index} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    padding: '25px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    borderLeft: `5px solid ${update.impact === 'High' ? '#ef4444' : update.impact === 'Medium' ? '#fbbf24' : '#22c55e'}`
                  }}>
                    {/* Update Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: '800', marginBottom: '10px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {update.title}
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{
                            background: 'rgba(148, 163, 184, 0.2)',
                            color: '#cbd5e1',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '1px solid rgba(148, 163, 184, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span>📅</span>
                            {new Date(update.date).toLocaleDateString()}
                          </span>
                          <span style={{
                            background: 'rgba(124, 58, 237, 0.2)',
                            color: '#c084fc',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '1px solid rgba(124, 58, 237, 0.3)'
                          }}>
                            {update.framework}
                          </span>
                        </div>
                      </div>
                      <span style={{
                        background: update.impact === 'High' ? '#ef4444' : update.impact === 'Medium' ? '#fbbf24' : '#22c55e',
                        color: '#ffffff',
                        padding: '8px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: `0 4px 15px ${update.impact === 'High' ? '#ef4444' : update.impact === 'Medium' ? '#fbbf24' : '#22c55e'}40`,
                        whiteSpace: 'nowrap'
                      }}>
                        {update.impact} Impact
                      </span>
                    </div>

                    {/* Update Description */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      padding: '15px',
                      marginBottom: '20px'
                    }}>
                      <p style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '600', lineHeight: '1.6', margin: 0 }}>
                        {update.description}
                      </p>
                    </div>

                    {/* Update Details */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                      {/* Action Required */}
                      <div style={{
                        background: 'rgba(59, 130, 246, 0.1)',
                        borderRadius: '12px',
                        padding: '15px',
                        border: '1px solid rgba(59, 130, 246, 0.3)'
                      }}>
                        <h4 style={{ color: '#60a5fa', fontSize: '15px', fontWeight: '800', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                          <span>📋</span>
                          Action Required:
                        </h4>
                        <p style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6', margin: 0 }}>
                          {update.actionRequired}
                        </p>
                      </div>

                      {/* Timeline Info */}
                      <div style={{
                        background: 'rgba(251, 191, 36, 0.1)',
                        borderRadius: '12px',
                        padding: '15px',
                        border: '1px solid rgba(251, 191, 36, 0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>⏰</span>
                            Deadline:
                          </span>
                          <span style={{ color: '#fbbf24', fontSize: '15px', fontWeight: '800', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {new Date(update.deadline).toLocaleDateString()}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>✓</span>
                            Status:
                          </span>
                          <span style={{ color: '#22c55e', fontSize: '15px', fontWeight: '800', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {update.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit Trail Tab */}
          {activeTab === 'audit' && (
            <div style={{ padding: '0' }}>
              <h2 style={{ color: '#ffffff', fontSize: '28px', fontWeight: '900', marginBottom: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                📝 Audit Trail
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: '16px', marginBottom: '30px', fontWeight: '500' }}>
                Complete audit trail of all compliance-related activities and changes
              </p>
              
              {/* Audit Timeline */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '30px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <div style={{ position: 'relative', paddingLeft: '40px' }}>
                  {/* Timeline vertical line */}
                  <div style={{
                    position: 'absolute',
                    left: '18px',
                    top: '10px',
                    bottom: '10px',
                    width: '3px',
                    background: 'linear-gradient(to bottom, #7c3aed, #a855f7, #c084fc)',
                    borderRadius: '2px'
                  }}></div>

                  {/* Timeline Entries */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                    {complianceData.auditTrail.map((entry, index) => (
                      <div key={index} style={{ position: 'relative', display: 'flex', gap: '20px' }}>
                        {/* Timeline dot */}
                        <div style={{
                          position: 'absolute',
                          left: '-29px',
                          top: '8px',
                          width: '16px',
                          height: '16px',
                          background: entry.impact === 'High' ? '#ef4444' : entry.impact === 'Medium' ? '#fbbf24' : '#22c55e',
                          borderRadius: '50%',
                          border: '3px solid #0f172a',
                          boxShadow: `0 0 15px ${entry.impact === 'High' ? '#ef4444' : entry.impact === 'Medium' ? '#fbbf24' : '#22c55e'}80`,
                          zIndex: 1
                        }}></div>

                        {/* Entry Content */}
                        <div style={{ flex: 1 }}>
                          {/* Date Badge */}
                          <div style={{
                            display: 'inline-block',
                            background: 'rgba(124, 58, 237, 0.2)',
                            color: '#c084fc',
                            padding: '6px 14px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '700',
                            border: '1px solid rgba(124, 58, 237, 0.3)',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            width: 'fit-content'
                          }}>
                            <span>📅</span>
                            {new Date(entry.date).toLocaleDateString()}
                          </div>

                          {/* Entry Card */}
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.03)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '16px',
                            padding: '20px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                          }}>
                            {/* Entry Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px', marginBottom: '12px', flexWrap: 'wrap' }}>
                              <h4 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '800', margin: 0, flex: 1, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                {entry.action}
                              </h4>
                              <span style={{
                                background: 'rgba(59, 130, 246, 0.2)',
                                color: '#60a5fa',
                                padding: '5px 12px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '700',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                whiteSpace: 'nowrap'
                              }}>
                                {entry.framework}
                              </span>
                            </div>

                            {/* Entry Description */}
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.03)',
                              borderRadius: '10px',
                              padding: '12px',
                              marginBottom: '12px'
                            }}>
                              <p style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '600', lineHeight: '1.6', margin: 0 }}>
                                {entry.description}
                              </p>
                            </div>

                            {/* Entry Meta */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                              <span style={{
                                color: '#94a3b8',
                                fontSize: '13px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <span style={{ fontSize: '16px' }}>👤</span>
                                <span style={{ color: '#cbd5e1', fontWeight: '700' }}>By: {entry.performedBy}</span>
                              </span>
                              <span style={{
                                background: entry.impact === 'High' ? '#ef4444' : entry.impact === 'Medium' ? '#fbbf24' : '#22c55e',
                                color: '#ffffff',
                                padding: '5px 14px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '800',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: `0 2px 10px ${entry.impact === 'High' ? '#ef4444' : entry.impact === 'Medium' ? '#fbbf24' : '#22c55e'}40`,
                                whiteSpace: 'nowrap'
                              }}>
                                {entry.impact} Impact
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default ESGRegulatoryCompliance;